import { format, parseISO } from 'date-fns';
import { ArrowRight, Loader2 } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { jobDayKey, windowLabel, type ScheduleMove } from './scheduleModel';

interface MoveConfirmDialogProps {
  move: ScheduleMove | null;
  timezone: string;
  techName: (techId: string | null) => string;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function slot(day: string | null, start: string | null, end: string | null, tech: string): string {
  const date = day ? format(parseISO(day), 'EEE d MMM') : 'No date';
  return `${tech} · ${date} · ${windowLabel({ scheduled_time_start: start, scheduled_time_end: end, scheduled_time: null })}`;
}

export function MoveConfirmDialog({ move, timezone, techName, pending, onConfirm, onCancel }: MoveConfirmDialogProps) {
  if (!move) return null;
  const { job } = move;
  const fromTech = job.assigned_tech_id;
  const techChanged = (fromTech ?? null) !== move.techId;
  const from = slot(
    jobDayKey(job, timezone),
    job.scheduled_time_start ?? job.scheduled_time,
    job.scheduled_time_end,
    techName(fromTech),
  );
  const to = slot(move.day, move.start, move.end, techName(move.techId));

  return (
    <AlertDialog open onOpenChange={(open) => { if (!open && !pending) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Move {job.job_number ?? 'this job'}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p className="text-foreground">{job.customer_name || 'Unknown customer'}</p>
              <div className="space-y-1.5 rounded-md border bg-muted/40 p-3 text-sm">
                <p className="text-muted-foreground">{from}</p>
                <p className="flex items-center gap-1.5 font-medium text-foreground">
                  <ArrowRight className="h-4 w-4 shrink-0 text-primary" />
                  {to}
                </p>
              </div>
              <p>
                {move.techId
                  ? `${techName(move.techId)} will be asked to accept it in the Steddy app.`
                  : 'It will be unassigned and taken off the technician\'s app.'}
                {techChanged && fromTech && move.techId ? ` ${techName(fromTech)} will no longer see it.` : ''}
              </p>
              {job.status === 'quote_sent' && (
                <p>This books the return visit for the accepted quote. The job goes back to Scheduled.</p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={(e) => { e.preventDefault(); onConfirm(); }}
          >
            {pending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Moving…</> : 'Move job'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
